import React, { useEffect, useMemo } from "react";
import { useAnalytics } from "../../context/AnalyticsContext";
import EntitySelector from "./index";
import PropTypes from "prop-types";

/**
 * Competitor selector dropdown.
 *
 * By default it reads/writes the global `selectedCompetitor` from AnalyticsContext.
 * When `externalValue` and `onExternalChange` are provided (e.g. for the R&D Landscape
 * page which maintains its own page-scoped competitor state), those props take precedence
 * and the global context selection is left untouched.
 *
 * When `exclusiveOptions` is true, only `customOptions` are shown — the global
 * competitors list is suppressed. Used by Product Showdown where available competitors
 * depend on which data has been scraped for the selected category.
 */
const CompetitorSelector = ({ customOptions = [], externalValue, onExternalChange, exclusiveOptions = false, label = "Competitors" }) => {
  const {
    selectedCompetitor,
    setSelectedCompetitor,
    companyId,
    competitors,
    loading
  } = useAnalytics();

  const usingExternal = externalValue !== undefined && onExternalChange !== undefined;
  const effectiveValue  = usingExternal ? externalValue  : selectedCompetitor;
  const effectiveChange = usingExternal ? onExternalChange : setSelectedCompetitor;

  // When exclusiveOptions is true, show only the provided customOptions (not global competitors)
  const displayedEntities = useMemo(() => {
    if (exclusiveOptions) return customOptions;
    return [...customOptions, ...competitors];
  }, [competitors, customOptions, exclusiveOptions]);

  // Auto-correct invalid selections: if the current value is not in the allowed list
  // for this page, reset to the first valid option.
  useEffect(() => {
    if (loading || competitors.length === 0) return;

    const isValidSelection = displayedEntities.some(
      (entity) => entity.id === effectiveValue
    );

    if (!isValidSelection && displayedEntities.length > 0) {
      effectiveChange(displayedEntities[0].id);
    }
  }, [effectiveValue, displayedEntities, competitors, loading, effectiveChange]);

  return (
    <EntitySelector
      entities={displayedEntities}
      selectedEntityId={effectiveValue}
      onEntityChange={effectiveChange}
      loading={!exclusiveOptions && loading && companyId}
      placeholder="Select Competitor"
      label={label}
      icon="business"
      loadingText="Loading competitors..."
      emptyResultText="No competitors found"
    />
  );
};

CompetitorSelector.propTypes = {
  customOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  externalValue: PropTypes.string,
  onExternalChange: PropTypes.func,
  exclusiveOptions: PropTypes.bool,
};

export default CompetitorSelector;
